import Footer from "../components/Footer";
import HeroPages from "../components/HeroPages";
import { capitalFirstLetter, currencyFormat } from "../utils";
import useFetch from "../hooks/useFetch";
import React from "react";
import Loading from "../components/Loading";
import NoFoundVehicles from "../components/NoFoundVehicles";
import PlaceholderImg from "../images/components/placeholder.jpg";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar2 from "../components/Navbar2";
import InputBox from "../components/InputBox";
import FilterModal from "../components/FilterModal";
import { ArrowRight } from "lucide-react";
import ProtectionModal from "../components/ProtectionModal";

function Models() {
  const [filters, setFilters] = React.useState({});
  const [sort, setSort] = React.useState("priceAsc");
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [protectionModalOpen, setProtectionModalOpen] = React.useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const getLanguage = typeof window !== undefined && localStorage.getItem("@wirasana_lang");
  const currentLanguage = JSON.parse(getLanguage);

  const { items: carsData, isLoading: carLoading } = useFetch({
    module: "cars",
    enabled: true,
  });

  const { items: pricesData } = useFetch({
    module: "car-prices",
    enabled: true,
  });

  const cars = carsData?.data;
  const prices = pricesData?.data;

  const _onSelectCar = () => {
    navigate("/");
  };

  const getDynamicPricing = (carId) => {
    const carPrices = prices?.filter((p) => p.car_id === carId) || [];

    if (carPrices.length === 0) {
      return {
        price: 0,
        label: "Contact for Price",
        priceType: null,
        showPrice: false,
      };
    }

    const hasSelfDrive = carPrices.some((p) => p.type === "self_drive" && p.condition_type === "default");
    const hasTour = carPrices.some((p) => p.type === "tour");
    const hasTransfer = carPrices.some((p) => p.type === "transfer_service");

    // 1. Self Drive (Highest Priority)
    if (hasSelfDrive) {
      const selfDrivePrice = carPrices.find((p) => p.type === "self_drive" && p.condition_type === "default");
      return {
        price: selfDrivePrice?.price || 0,
        label: t("daily_rate_from"),
        priceType: "self_drive",
        showPrice: true,
        additionalText: "",
      };
    }

    // 2. Tour Only (No Self Drive)
    if (hasTour && !hasSelfDrive) {
      const tourPrices = carPrices.filter((p) => p.type === "tour");

      // Get lowest tour price
      const lowestTour = tourPrices.reduce((min, current) => {
        return current.price < min.price ? current : min;
      }, tourPrices[0]);

      const hourPackage = lowestTour?.condition?.hour || 6;

      return {
        price: lowestTour?.price || 0,
        label: t("tour_package"),
        priceType: "tour",
        showPrice: true,
        hour: hourPackage,
        additionalText: ` /${hourPackage}${currentLanguage?.value === "id" ? " Jam" : "h"}`,
      };
    }

    // 3. Transfer Service Only (Lowest Priority)
    if (hasTransfer && !hasSelfDrive && !hasTour) {
      const transferPrices = carPrices.filter((p) => p.type === "transfer_service");

      // Get lowest transfer price
      const lowestTransfer = transferPrices.reduce((min, current) => {
        return current.price < min.price ? current : min;
      }, transferPrices[0]);

      const areaName = lowestTransfer?.condition?.areas?.name || "";

      return {
        price: lowestTransfer?.price || 0,
        label: t("airport_transfer_from"),
        priceType: "transfer_service",
        showPrice: true,
        area: areaName,
        additionalText: areaName ? ` (${areaName})` : "",
      };
    }

    return {
      price: 0,
      label: t("contact_for_price") || "Contact for Price",
      priceType: null,
      showPrice: false,
      additionalText: "",
    };
  };

  // Get car price for filtering and sorting
  const getCarPrice = (car) => {
    const pricingInfo = getDynamicPricing(car.id);
    return pricingInfo.price || 0;
  };

  // Filter mobil
  const filteredCars = cars?.filter((item) => {
    const searchString = JSON.stringify(item).toLowerCase();
    const matchesSearch = filters.search ? searchString.includes(filters.search.toLowerCase()) : true;

    const carPrice = getCarPrice(item);
    const matchesPrice =
      filters.price?.length === 2 ? carPrice >= filters.price[0] && carPrice <= filters.price[1] : true;

    const matchesPassenger =
      filters.passengers?.length > 0 ? filters.passengers.includes(item.additional_informations?.seat) : true;

    const matchesTransmission =
      filters.transmission?.length > 0 ? filters.transmission.includes(item.transmision) : true;

    const matchesDrivetrain = filters.drivetrain?.length > 0 ? filters.drivetrain.includes(item.drive_system) : true;

    const matchesVehicleType = filters.vehicleType ? item.vehicle_type === filters.vehicleType : true;

    return (
      matchesSearch &&
      matchesPrice &&
      matchesPassenger &&
      matchesTransmission &&
      matchesDrivetrain &&
      matchesVehicleType
    );
  });

  // Urutkan mobil
  const sortedCars = React.useMemo(() => {
    if (!filteredCars) return [];

    const carsCopy = [...filteredCars];

    switch (sort) {
      case "priceAsc":
        return carsCopy.sort((a, b) => {
          const priceA = getCarPrice(a);
          const priceB = getCarPrice(b);
          return priceA - priceB;
        });
      case "priceDesc":
        return carsCopy.sort((a, b) => {
          const priceA = getCarPrice(a);
          const priceB = getCarPrice(b);
          return priceB - priceA;
        });
      case "nameAsc":
        return carsCopy.sort((a, b) => a.name.localeCompare(b.name));
      case "nameDesc":
        return carsCopy.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return carsCopy;
    }
  }, [filteredCars, sort, prices]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleApplyFilters = (f) => {
    setFilters(f);
  };

  const handleSortChange = (val) => {
    setSort(val);
  };

  return (
    <>
      <section className="models-section">
        <Navbar2 />
        <HeroPages name={t("vehicles")} />
        <InputBox sort={sort} setFilterOpen={() => setFilterOpen(true)} handleSortChange={handleSortChange} />
        <FilterModal open={filterOpen} onClose={() => setFilterOpen(false)} onApply={handleApplyFilters} />

        <div className="container">
          {!sortedCars?.length && !carLoading && <NoFoundVehicles title="Vehicles is empty" />}
          {carLoading ? (
            <Loading />
          ) : (
            <div className="grid grid-cols-1 gap-6 my-24 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {sortedCars?.map((item, idx) => {
                const newPrices = prices?.filter((p) => p.car_id === item?.id);

                // Get dynamic pricing info
                const pricingInfo = getDynamicPricing(item.id);

                return (
                  <div
                    key={idx}
                    className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100 hover:border-[#f5272b]/30"
                  >
                    {/* Image Section */}
                    <div className="relative flex items-center justify-center w-full h-56 overflow-hidden bg-white">
                      <img
                        src={item?.image_urls[0] || PlaceholderImg}
                        alt={item.name}
                        className="object-contain w-full h-full transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 right-4">
                        <Link
                          to={`/detail-vehicle`}
                          state={{
                            car: item,
                            prices: newPrices,
                            from: "models",
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm hover:bg-[#f5272b] text-slate-800 hover:text-white rounded-md shadow-lg transition-all duration-300 text-sm font-semibold"
                        >
                          <i className="fa-solid fa-circle-info"></i>
                          <span>{t("more_info")}</span>
                        </Link>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 space-y-5">
                      {/* Title */}
                      <div>
                        <h3 className="mb-1 text-2xl font-bold text-slate-900">{capitalFirstLetter(item.name)}</h3>
                      </div>

                      {/* UPDATED PRICE SECTION - DYNAMIC PRICING */}
                      <div className="p-4 border rounded-lg bg-gradient-to-r from-slate-50 to-transparent border-slate-200">
                        {pricingInfo.showPrice && pricingInfo.price > 0 ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-600">{pricingInfo.label}</p>
                            <p className="text-3xl font-bold text-[#f5272b]">
                              {currencyFormat(pricingInfo.price)}
                              {pricingInfo.additionalText && (
                                <span className="ml-2 text-base font-normal text-slate-500">
                                  {pricingInfo.additionalText}
                                </span>
                              )}
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-[#f5272b] text-sm font-semibold">{pricingInfo.label}</p>
                          </div>
                        )}

                        <div className="pt-3 mt-3 ">
                          <button
                            onClick={() => setProtectionModalOpen(true)}
                            className="flex items-start w-full gap-2 p-3 text-left transition-all duration-200 border border-green-100 rounded-lg bg-green-100/50"
                          >
                            <i className="fa-solid fa-circle-check text-green-600 mt-0.5 flex-shrink-0"></i>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-semibold text-slate-900">{t("includes")}: </span>
                                <span className="text-[#f5272b] font-medium text-sm">{t("basic_protection")}</span>
                                <i className="flex-shrink-0 ml-1 text-xs fa-solid fa-circle-info text-slate-400"></i>
                              </div>

                              <div className="mt-0.5">
                                <span className="text-xs text-slate-600">{t("km_unlimited")}</span>
                              </div>
                              <div className="mt-0.5 flex items-center text-blue-600 gap-1">
                                <span className="text-xs">{t("see_more_details")}</span>
                                <ArrowRight className="w-4 h-4" />
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Specifications Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-tag text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Brand</p>
                            <p className="text-sm font-semibold truncate text-slate-900">
                              {capitalFirstLetter(item.brands)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-gears text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Trans.</p>
                            <p className="text-sm font-semibold truncate text-slate-900">
                              {capitalFirstLetter(item.transmision)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-car text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Drive</p>
                            <p className="text-sm font-semibold truncate text-slate-900">{item?.drive_system}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-users text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Seats</p>
                            <p className="text-sm font-semibold text-slate-900">
                              {item?.additional_informations?.seat} {t("passenger")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Book Button */}
                      <button
                        onClick={() => _onSelectCar()}
                        className="w-full px-6 py-4 bg-[#f5272b] hover:bg-red-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                      >
                        <span className="flex items-center justify-center gap-3">
                          {t("book_ride").toUpperCase()}
                          <i className="transition-transform fa-solid fa-arrow-right group-hover:translate-x-1"></i>
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Footer />
        <ProtectionModal open={protectionModalOpen} onClose={() => setProtectionModalOpen(false)} />
      </section>
    </>
  );
}

export default Models;
